import React from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { captureException } from '@sentry/react';
import { useQuery } from '@tanstack/react-query';

import { Case, useAlertsApi, useCasesApi } from '../../api';
import Frame from '../../components/Library/Frame';
import { Modal, ModalBody, ModalHeader } from '../../components/Library/Modal';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useAPI } from '../../libs/libAPI';
import { formatToLocalTime } from '../../libs/libDate';
import { camelCaseToWords, snakeCaseToWords } from '../../libs/libString';
import { enumKeyByValue, sortBy } from '../../libs/utils';
import { CaseOrigin, CaseSeverity, CaseStatus, CaseType } from '../../types/CaseEnums';
import WDCaseDetails, { WDCaseDetailsSchema } from '../../types/WDCaseDetails';
import CaseActivityView from './CaseActivity';

interface Detail {
  label: string;
  getValue: (caseDetail: Case) => React.ReactNode;
}

const DETAILS: Detail[] = [
  {
    label: 'Case Number',
    getValue: (c) => c.caseNumber
  },
  {
    label: 'Created On',
    getValue: (c) => formatToLocalTime(c.createdAt)
  },
  {
    label: 'Case Status',
    getValue: (c) => snakeCaseToWords(c.status)
  },
  // {
  //   label: 'Case Type',
  //   getValue: (c) => enumKeyByValue(CaseType, c.caseType)
  // },
  {
    label: 'Requester',
    getValue: (c) => c.createdByName
  },
  // {
  //   label: 'Case Origin',
  //   getValue: (c) => c.origin != null ? enumKeyByValue(CaseOrigin, c.origin) : <i>null</i>
  // },
  {
    label: 'Case Severity',
    getValue: (c) => c.severity == null ? <i>Null</i> : snakeCaseToWords(c.severity as string)
  }
];

const CaseDetails: React.FC = () => {
  const navigate = useNavigate();
  const [queryParams] = useSearchParams();

  const closeCase = (): void => {
    navigate(`..${decodeURIComponent(queryParams.get('casesQuery') ?? '')}`);
  };

  return <>
    <Modal show={true} closeModal={closeCase} modalSize='Screen'>
      <InnerCaseDetails />
    </Modal>
  </>;
};

const InnerCaseDetails: React.FC = () => {
  const { caseId } = useParams();
  const [queryParams] = useSearchParams();
  const alertsApi = useAlertsApi();
  const casesApi = useCasesApi();
  const navigate = useNavigate();

  const caseDetailsQuery = useQuery(['case', caseId], async () => {
    const caseInfo = await casesApi.get_case_by_id({
      params: {
        case_id: caseId ?? 'null'
      }
    })

    return caseInfo;
  });

  const caseActivityQuery = useQuery(['case', caseId, 'activity'], async () => {
    const activites = await casesApi.query_case_activities({
      params: {
        case_id: caseId ?? 'null'
      }
    });

    return activites.sort(sortBy('createdDate', true));
  });

  const relatedAlertsQuery = useQuery(['case', caseId, 'alerts'], async () => {
    const relatedAlerts = await alertsApi.query_alerts({
      queries: {
        filter: `related_case_id eq '${caseId}'`
      }
    });

    return relatedAlerts.sort(sortBy('alert_time'));
  });

  const closeCase = (): void => {
    navigate(`..${decodeURIComponent(queryParams.get('casesQuery') ?? '')}`);
  };

  if (caseDetailsQuery.isLoading) {
    return <>
      <ModalHeader closeModal={closeCase}>Loading...</ModalHeader>
      <ModalBody>
        <div className='flex w-full pt-2'>
          <div className='mx-auto mt-20 text-center'>
            <LoadingSpinner className='h-32 w-32' />
            <p>Loading Case Details</p>
          </div>
        </div>
      </ModalBody>
    </>;
  }

  if (caseDetailsQuery.isError) {
    captureException(caseDetailsQuery.error);
    return <></>;
  }

  const caseDetails = caseDetailsQuery.data;

  return <>
    <ModalHeader closeModal={closeCase}>{caseDetails.title != null ? caseDetails.title : <i>Missing Subject</i>}</ModalHeader>
    <ModalBody className='overflow-y-auto'>
      <div className='lg:flex w-full'>
        <div className='lg:w-96 pb-2'>
          <Frame varient='nav' title={<span className='text-xl font-bold'>Details</span>}>
            <table className='w-full'>
              <tbody>
                {DETAILS.map((detail) => (
                  <tr key={detail.label}>
                    <td className='font-bold py-1 pr-1'>{detail.label}</td>
                    <td>{detail.getValue(caseDetails)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Frame>

          {relatedAlertsQuery.data != null && relatedAlertsQuery.data.length > 0 && <>
            <Frame varient='nav' title={<span className='text-xl font-bold'>Related Alerts</span>}>
              {relatedAlertsQuery.data.map((alert) => (
                <div key={alert.id}>
                  <div>{alert.alert_time == null ? 'No Alert Time' : formatToLocalTime(alert.alert_time)}</div>
                  <div className='overflow-ellipsis'><Link to={`/alerts/${alert.id}`}><span>{alert.name}</span></Link></div>
                </div>
              ))}
            </Frame>
          </>}

        </div>
        <div className='lg:flex-1 max-w-[100vw] lg:max-w-[calc(100vw-544px)] max-h-full overflow-y-auto'>
          <Frame varient='nav' title={<span className='text-xl font-bold'>Activities</span>}>
            {caseActivityQuery.data?.map((logEntry) => <CaseActivityView caseId={caseDetails.id} activity={logEntry} key={logEntry.id} />)}
          </Frame>
        </div>
      </div>
    </ModalBody>
  </>;
};

export default CaseDetails;
